import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FormFieldList, SmartFormComponent } from 'ngx-smart-form';
import {
  filter,
  map,
  Observable,
  startWith,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { Account, BEAccount } from '../../../interfaces/account';
import { AuthService } from '../../_core/services/auth.service';
import { DialogService } from '../../_core/services/dialog.service';

interface DataService<D = any, F = D> {
  search(
    pageNumber: number,
    pageSize: number,
    filter?: Partial<F>,
    ...args: unknown[]
  ): Observable<{ total: number; items: D }>;
}

@Component({
  standalone: true,
  selector: 'app-server-side-table',
  templateUrl: './server-side-table.component.html',
  styleUrls: ['./server-side-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class ServerSideTableComponent<D = any, F = D>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild('searchFormComponent')
  searchFormComponent?: SmartFormComponent<F>;

  initialSearchTerms: Partial<F> = {};

  fields: FormFieldList<F> = [];

  totalData = 0;

  pageSizes = [5, 10, 15];

  displayedColumns: (keyof D | string)[] = [];
  dataSource = new MatTableDataSource<D>([]);
  protected dataService: DataService;

  private tableSubscription?: Subscription;
  private meSubscription?: Subscription;

  protected auth: AuthService;
  protected cdr: ChangeDetectorRef;

  constructor(protected inj: Injector, dataService: Type<DataService>) {
    this.dataService = this.inj.get(dataService);
    this.auth = this.inj.get(AuthService);
    this.cdr = this.inj.get(ChangeDetectorRef);
  }

  ngOnInit(): void {
    this.meSubscription = this.auth.me$
      .pipe(filter((data) => data.state === 'completed'))
      .subscribe((data) => {
        this.fields = this.genFilter ? this.genFilter() : [];
        let formValue: Partial<F> = {};
        this.fields
          .flat()
          .forEach((field) => (formValue[field.formControlName] = field.value));
        this.initialSearchTerms = formValue;
      });
  }

  ngAfterViewInit() {
    this.tableSubscription = this.initTable().subscribe();
  }

  ngOnDestroy(): void {
    this.tableSubscription?.unsubscribe();
    this.meSubscription?.unsubscribe();
  }

  protected initTable() {
    return this.searchOnPaginationEvent();
  }

  protected searchOnPaginationEvent() {
    this.dataSource.paginator = this.paginator!;

    return this.paginator!.page.pipe(
      startWith({}),
      switchMap(() => {
        return this.getFilterData$(
          this.paginator?.pageIndex,
          this.paginator?.pageSize,
          this.searchFormComponent?.formRawValue
        );
      })
    );
  }

  protected getFilterData$(
    pageNumber: number = this.paginator?.pageIndex || 0,
    pageSize: number = this.paginator?.pageSize || 0,
    filter: Partial<F> | undefined = this.searchFormComponent?.formRawValue
  ): Observable<D[]> {
    return this.search(pageNumber, pageSize, filter).pipe(
      map((response) => {
        this.totalData = response?.total || 0;

        return response ? response.items : [];
      }),
      tap((items) => {
        this.dataSource = new MatTableDataSource(items);
        this.cdr.detectChanges();
      })
    );
  }

  protected search(
    pageNumber: number,
    pageSize: number,
    filter?: Partial<F>
  ): Observable<{ items: D[]; total: number }> {
    return this.dataService.search(pageNumber + 1, pageSize, filter);
  }

  removeFilter() {
    this.searchFormComponent?.form.reset(this.initialSearchTerms as any);
    this.paginator!.pageIndex = 0;
    this.getFilterData$(0, this.paginator!.pageSize).subscribe();
  }

  applyFilter() {
    this.paginator!.pageIndex = 0;
    this.getFilterData$(
      0,
      this.paginator!.pageSize,
      this.searchFormComponent?.formRawValue
    ).subscribe();
  }

  genFilter?(...args: unknown[]): FormFieldList<F>;
}
